import { useId, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import type { RoiProject } from '@wr/shared';
import { retention } from '../../../../../config/portal.json';
import { ApiError, del, errorMessage, post } from '../../api.js';
import { useToast } from '../../components/Toast.js';
import { Dialog, Field, Notice } from '../../components/ui.js';
import { newId } from '../../offline/ids.js';
import { useProject } from './RoiProject.js';

/** The message of a scenario name already used for this calculator and client. */
export const DUPLICATE_SCENARIO =
  'This scenario already exists for this client and calculator. Choose another name.';

export const isDuplicateScenario = (error: unknown) =>
  error instanceof ApiError && error.status === 409 && error.body.reason === 'duplicate_scenario';

/** Delete: hidden at once, an admin can restore it for the ROI trash period (brief section 7.7). */
export function DeleteProject() {
  const { project } = useProject();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = () => navigate('..', { replace: true });

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await del(`/roi/projects/${project.id}`);
      toast('Project deleted');
      navigate('/roi', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Delete this project?"
      onClose={close}
      footer={
        <>
          <button type="button" className="btn" autoFocus onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={busy}
            onClick={() => void confirm()}
          >
            Delete
          </button>
        </>
      }
    >
      <p>
        It disappears from the list. An administrator can recover it for {retention.roiTrashDays}{' '}
        days.
      </p>
      {error && <Notice kind="error">{error}</Notice>}
    </Dialog>
  );
}

/** Duplicate: a copy of one's own, from any project, which one may then change. */
export function DuplicateProject() {
  const { project } = useProject();
  const navigate = useNavigate();
  const toast = useToast();
  const formId = useId();
  const [name, setName] = useState(`${project.scenarioName} (copy)`);
  const [nameError, setNameError] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const close = () => navigate('..', { replace: true });

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!name.trim()) {
      setNameError('Required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const copy = await post<RoiProject>(`/roi/projects/${project.id}/duplicate`, {
        id: newId(),
        scenarioName: name.trim(),
      });
      toast(`Copy saved: ${copy.scenarioName}`);
      navigate(`/roi/${copy.id}`, { replace: true });
    } catch (err) {
      if (isDuplicateScenario(err)) setNameError(DUPLICATE_SCENARIO);
      else setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      small
      title="Duplicate this project"
      onClose={close}
      footer={
        <>
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn primary" disabled={busy}>
            Duplicate
          </button>
        </>
      }
    >
      <form id={formId} className="dlg-form" noValidate onSubmit={(e) => void submit(e)}>
        <p className="note">
          The copy is yours: you can change it. It keeps the same linked record and figures.
        </p>
        <Field
          label="Scenario Name"
          mark
          required
          value={name}
          error={nameError}
          onChange={(e) => {
            setName(e.target.value);
            setNameError(undefined);
          }}
        />
        {error && <Notice kind="error">{error}</Notice>}
      </form>
    </Dialog>
  );
}
